import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { isDirty } from 'redux-form';

import history from '../../../history';
import { messages as acctSetMsgs } from './AccountSettings.i18n';
import { messages as confirmMsgs } from '../../../components/Person/Contact/Confirmation.i18n';
import * as MW from '../../../redux/modules/personal-contacts/personal-contacts-middleware';
import * as MsgBarActions from '../../../redux/modules/system-shared/message-bar-action-creator';
import * as conf from './AccountSettingsConfig';
import Confirmation from '../../../components/Person/Contact/Confirmation';
import * as action from '../../../redux/modules/personal-contacts/personal-contacts-action-creator';

class AccountSettings extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showModal: false,
      showSaveModal: false,
      modalAttributes: null,
      saveModalAttributes: null,
    };
  }

  componentWillUnmount() {
    this.props.dispatch(action.clearFlags());
  }

  getUserAccountSettingUiState(userAccountState) {
    const { personalContacts, selectedContactId } = this.props;
    const patientContact = personalContacts.find(
      (c) => c.contactId === selectedContactId);

    if (patientContact && patientContact.userInfo) {
      return userAccountState.isUserAccountAccessEnabled ?
        this.activeUserAccount(patientContact, userAccountState.isUserAccountNotLocked) :
        this.inactiveUserAccount(patientContact);
    }

    if (patientContact && userAccountState.inviteContactEnabled) {
      if (!userAccountState.arInvitationExists) {
        return this.noUserAccount(patientContact);
      }
      return this.invitedContact(patientContact);
    }
    return this.noAccountNoInvite();
  }

  handleDirtyContact = (normalFlow, patientContact) => {
    const { isDirtyForm, submitContact } = this.props;
    if (isDirtyForm) {
      this.handleOpenSaveModal(
        {
          formattedMessages: confirmMsgs.contactIsDirty,
          handleSubmit: () => {
            this.handleCloseSaveModal();
            submitContact((values) => normalFlow(values));
          },
          handleClose: () => {
            this.handleCloseSaveModal();
            normalFlow(patientContact);
          },
        }
      );
      return true;
    }
    return false;
  };

  handleInviteButton = (patientContact) => () => {
    const normalFlow = (contact) => {
      if (contact && !contact.contactEmail) {
        this.props.dispatch(MsgBarActions.showErrorMessage('contactEmailRequiredForInvite'));
      } else {
        history.push('/invite-auth-statement');
      }
    };
    if (!this.handleDirtyContact(normalFlow, patientContact)) {
      normalFlow(patientContact);
    }
  };

  // isUserAccountNotLocked === false,
  // show "acctSetMsgs.accountSettings.state.lockAccount" message
  generateStateMessage = (isUserAccountNotLocked) =>
    ((isUserAccountNotLocked) ?
      conf.renderStateMessage(acctSetMsgs.accountSettings.state.accessEnabled) :
      conf.renderStateMessage(acctSetMsgs.accountSettings.state.lockAccount, 'color-required'));

  // !isUserAccountNotLocked or === false means the account is locked, show Unlock button
  activeUserAccount = (patientContact, isUserAccountNotLocked) => ({
    renderStateMessage: (patientContact.userInfo) ?
      this.generateStateMessage(isUserAccountNotLocked) : null,
    renderUsername: conf.renderUserName(patientContact.userInfo.userName),
    renderButtons: (ariaDescribedby) => (
      [
        {
          qaTag: 'unlockButton',
          key: 'unlockKey',
          ariaDescribedby,
          label: acctSetMsgs.accountSettings.button.unlock,
          onClick: () => {
            const normalFlow = (contact) => {
              this.props.dispatch(MW.unlockPatientContactUserAccount(contact));
            };
            if (!this.handleDirtyContact(normalFlow, patientContact)) {
              normalFlow(patientContact);
            }
          },
        },
        {
          qaTag: 'resetPasswordButton',
          key: 'resetPasswordButtonkey',
          ariaDescribedby,
          label: acctSetMsgs.accountSettings.button.resetPassword,
          onClick: () => {
            const normalFlow = (contact) => {
              if (!contact.userInfo.isEmailConfirmed) {
                this.props.dispatch(MsgBarActions.showErrorMessage('resetContactPasswordEmailNotConfirmed'));
                return;
              }

              this.handleOpenModalClick({
                formattedMessages: confirmMsgs.resetPassword,
                formattedMessageValues: {
                  instruction: {
                    userEmailAddress: contact.contactEmail,
                  },
                },
                handleSubmit: () => {
                  this.props.dispatch(MW.resetPatientContactPassword(contact));
                  this.handleCloseModalClick();
                  this.props.dispatch(MsgBarActions.showSuccessMessage('contactPasswordResetProgress', [
                    (<span className="fa fa-spinner fa-spin"></span>),
                  ]));
                },
                handleClose: this.handleCloseModalClick,
              })();
            };
            if (!this.handleDirtyContact(normalFlow, patientContact)) {
              normalFlow(patientContact);
            }
          },
        },
        {
          qaTag: 'disableAccessButton',
          key: 'disableAccessButtonkey',
          ariaDescribedby,
          label: acctSetMsgs.accountSettings.button.disableAccess,
          onClick: this.clickChangeAccess(patientContact, confirmMsgs.disableAccess, false),
        },
      ]
        .filter((b) => b.qaTag !== 'unlockButton' || (b.qaTag === 'unlockButton' && !isUserAccountNotLocked))
        .map((b) => conf.renderButton(b))),
  });

  clickChangeAccess = (patientContact, message, enable) => () => {
    const normalFlow = (contact) => {
      this.handleOpenModalClick(
        {
          formattedMessages: message,
          handleSubmit: () => {
            this.props.dispatch(MW.changePatientContactAccess(contact, enable));
            this.handleCloseModalClick();
          },
          handleClose: this.handleCloseModalClick,
        })();
    };
    if (!this.handleDirtyContact(normalFlow, patientContact)) {
      normalFlow(patientContact);
    }
  }

  inactiveUserAccount = (patientContact) => ({
    renderStateMessage: conf.renderStateMessage(acctSetMsgs.accountSettings.state.accessDisabled, 'color-required'),
    renderUsername: conf.renderUserName(patientContact.userInfo.userName),
    renderButtons: (ariaDescribedby) => (
      [
        {
          qaTag: 'enableAccessButton',
          key: 'enableAccessButtonkey',
          ariaDescribedby,
          label: acctSetMsgs.accountSettings.button.enableAccess,
          onClick: this.clickChangeAccess(patientContact, confirmMsgs.enableAccess, true),
        },
      ].map((b) => conf.renderButton(b))),
  });

  noUserAccount = (patientContact) => ({
    renderStateMessage: conf.renderStateMessage(acctSetMsgs.accountSettings.state.noUserAccount),
    renderUsername: () => null,
    renderButtons: (ariaDescribedby) => ([
      (
        <a role="button" className="btn btn-outline-secondary btn-light mb-1"
          tabIndex="0"
          data-qa-item="inviteButton"
          key="inviteButtonkey"
          style={{ marginRight: '3px' }}
          aria-describedby={ariaDescribedby}
          onClick={this.handleInviteButton(patientContact)}
        >
          <FormattedMessage { ...acctSetMsgs.accountSettings.button.inviteContact } />
        </a>
      ),
    ]),
  });

  clickReInvite = (patientContact) => () => {
    const normalFlow = (contact) => {
      this.handleOpenModalClick({
        formattedMessages: confirmMsgs.resendInvite,
        formattedMessageValues: { instruction: {
          contactEmail: contact.contactEmail,
        } },
        handleSubmit: () => {
          this.handleInviteButton(contact)();
          this.handleCloseModalClick();
        },
        handleClose: this.handleCloseModalClick,
      })();
    };
    if (!this.handleDirtyContact(normalFlow, patientContact)) {
      normalFlow(patientContact);
    }
  }

  invitedContact(patientContact) {
    return ({
      renderStateMessage: conf.renderStateMessage(acctSetMsgs.accountSettings.state.invitedContactMessage),
      renderUsername: () => null,
      renderButtons: (ariaDescribedby) => ([
        (
          <a role="button" className="btn btn-outline-secondary btn-light mb-1"
            tabIndex="0"
            data-qa-item="reinviteButton"
            key="reinviteButtonkey"
            aria-describedby={ariaDescribedby}
            onClick={this.clickReInvite(patientContact)}
          >
            <FormattedMessage { ...acctSetMsgs.accountSettings.button.reinviteContact } />
          </a>
        ),
      ]),
    });
  }

  handleOpenSaveModal = (saveModalAttributes) => {
    this.setState({
      showSaveModal: true,
      saveModalAttributes,
    });
  }

  handleCloseSaveModal = () => {
    this.setState({
      showSaveModal: false,
      saveModalAttributes: null,
    });
  }

  handleOpenModalClick = (modalAttributes) => () => {
    this.setState({
      showModal: true,
      modalAttributes,
    });
  }

  handleCloseModalClick = () => {
    this.setState({
      showModal: false,
      modalAttributes: null,
    });
  }

  initializeAccountUnlockedValue = (accountUnlocked, patientContact) => {
    if (accountUnlocked) {
      return accountUnlocked;
    }
    // return TRUE if account is not locked otherwise FALSE for locked account;
    if (patientContact && patientContact.userInfo) {
      return !patientContact.userInfo.isLockedOut ||
        patientContact.userInfo.minutesLeftBeforeLockoutExpires === null;
    }

    return false;
  }

  render() {
    const {
      personalContacts,
      selectedContactId,
      accessEnabled,
      accountUnlocked,
      inviteContactEnabled,
      arInvitationExists,
    } = this.props;

    const patientContact = personalContacts.find(
      (c) => c.contactId === selectedContactId
    );

    const isUserAccountAccessEnabled = (accessEnabled !== null) ?
      accessEnabled : patientContact.isAuthorizedRepresentativeActive;
    const isUserAccountNotLocked = this.initializeAccountUnlockedValue(accountUnlocked, patientContact);
    let userAccountState = {
      isUserAccountAccessEnabled,
      isUserAccountNotLocked,
      inviteContactEnabled,
      arInvitationExists,
    };

    if (patientContact) {
      userAccountState = this.getUserAccountSettingUiState(userAccountState);
    }

    if (!userAccountState) {
      return null;
    }

    const {
      modalAttributes,
      saveModalAttributes,
    } = this.state;

    return (
      <div className="contact-section">
        <h4 className="mt-2" data-qa-item="sectionHeader" tabIndex="0">
          <FormattedMessage { ...acctSetMsgs.accountSettings.title } />
        </h4>

        {userAccountState && userAccountState.renderStateMessage()}

        {userAccountState && userAccountState.renderUsername()}

        {userAccountState && userAccountState.renderButtons('buttonHint')}

        <Confirmation
          showModal={ this.state.showSaveModal }
          {...saveModalAttributes}
        />
        <Confirmation
          showModal={ this.state.showModal }
          {...modalAttributes}
        />
        <span id="buttonHint" className="sr-only">
          <FormattedMessage { ...acctSetMsgs.accountSettings.button.hint } />
        </span>
      </div>
    );
  }
}

AccountSettings.propTypes = {
  dispatch: PropTypes.func,
  personalContacts: PropTypes.array,
  selectedContactId: PropTypes.string,
  accessEnabled: PropTypes.bool,
  accountUnlocked: PropTypes.bool,
  inviteContactEnabled: PropTypes.bool,
  contactEmail: PropTypes.string,
  arInvitationExists: PropTypes.bool,
  formState: PropTypes.object,
  submitContact: PropTypes.func,
  isDirtyForm: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  personalContacts: state.personalContacts.personalContacts,
  selectedContactId: state.personalContacts.selectedContactId,
  accessEnabled: state.personalContacts.accessEnabled,
  accountUnlocked: state.personalContacts.accountUnlocked,
  arInvitationExists: state.invitation.arInvitationExists,
  formState: state.form.contactform,
  isDirtyForm: isDirty('contactform')(state),
});

export default connect(mapStateToProps)(AccountSettings);